import { Button, Pagination, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import {
  ListingComponentContext,
  ListingComponentProvider,
} from "../../components/common/listingComponents/ListingComponentContext";
import ListingComponentWrapper from "../../components/common/listingComponents/ListingComponentWrapper";
// ToastNotification replaced with ToastContainer from pepsico-ds

import CustomizedRewardsGrid from "../../components/customizedRewards/CustomizedRewardsGrid";
import CustomizedRewardsTable from "../../components/customizedRewards/CustomizedRewardsTable";
import { deleteCustomizedEarningById } from "../../graphql/mutations/deleteCustomizedEarningById";
import { findEarnings } from "../../graphql/queries/listCustomizedEarnings";
import { useDeleteConfirmation } from "../../hooks/useDeleteConfirmation";
// useToast hook no longer needed as we're using ToastContainer directly
import { paths } from "../../routes/paths";
import {
  getUniqueCampaignCount,
  getUniqueRewardCount,
} from "../../utils/rewardMappingUtils";
import "./customizedRewards.scss";
import {
  customizedRewardsFilterJsonSchema,
  customizedRewardsFilterUiSchema,
  customizeFilterDebounceJsonSchema,
  customizeFilterDebounceUiSchema,
} from "./mocks";

export const CustomizedRewardsListPage = () => {
  const ROWS_PER_PAGE = 12;
  return (
    <ListingComponentProvider
      query={findEarnings}
      fetchPolicy="cache-and-network"
      queryKey="findEarnings"
      noPagination={true}
      initialFilterData={{
        limit: ROWS_PER_PAGE,
        offset: 0, // Start at page 0
      }}
    >
      <CustomizedRewardsListContent rowsPerPage={ROWS_PER_PAGE} />
    </ListingComponentProvider>
  );
};

const CustomizedRewardsListContent = ({ rowsPerPage }) => {
  const { filterData, setFilterData, viewableRecords, viewType, refetch } =
    useContext(ListingComponentContext);
  const [toastData, setToastData] = useState([]);
  const navigate = useNavigate();

  // Use shared delete confirmation hook with custom success handler
  const { deleteModal, openDeleteModal, closeDeleteModal, performDelete } =
    useDeleteConfirmation(
      deleteCustomizedEarningById,
      refetch,
      "Customized reward deleted successfully.",
      "Something went wrong.",
      () => {
        // Add success toast notification
        setToastData([
          {
            type: "success",
            text: "Customized reward deleted successfully.",
          },
        ]);
      }
    );

  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = Math.ceil((viewableRecords?.total || 0) / rowsPerPage);

  useEffect(() => {
    if (filterData?.offset !== undefined) {
      setCurrentPage(filterData.offset + 1);
    }
  }, [filterData]);

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
      offset: 0, // Start at page 0
      limit: rowsPerPage,
    }));
  }, [setFilterData, rowsPerPage]);

  // Removed onTap functionality

  const handleChangePage = (page) => {
    setFilterData((prev) => ({
      ...prev,
      offset: page - 1, // Send 0-indexed page number
    }));
    setCurrentPage(page);
  };

  const handleViewReward = (item) => {
    navigate(`/${paths.rewards}/${paths.customized_reward_details}/${item.id}`);
  };

  const handleDeleteReward = (item) => {
    openDeleteModal(item);
  };

  const handleCreateReward = () => {
    navigate(`/${paths.rewards}/${paths.customized_rewards}/${paths.create}`);
  };

  const handleEditReward = (item) => {
    navigate(
      `/${paths.rewards}/${paths.customized_rewards}/${paths.edit}/${item.id}`
    );
  };

  // Utility functions imported from rewardMappingUtils

  // Process data to include calculated counts
  const processedRewardsData = (viewableRecords?.items || []).map((item) => ({
    ...item,
    uniqueCampaignCount: getUniqueCampaignCount(item.rewardMappings),
    uniqueRewardCount: getUniqueRewardCount(item.rewardMappings),
  }));

  return (
    <div className="px-3 pt-3">
      {Array.isArray(toastData) && toastData?.length > 0 && (
        <ToastContainer
          className="customized-rewards-toast"
          data={toastData}
          removeToast={() => setToastData([])}
          showActionIcon
        />
      )}
      <DeleteConfirmationModal
        isOpen={deleteModal.open}
        title="Delete the customized reward?"
        onConfirm={performDelete}
        onCancel={closeDeleteModal}
      />
      <div className="container-card">
        <div className="page-content">
          <div className="content-header">
            <h2>Customized Rewards</h2>
            <Button size="medium" iconTrailing="add" onClick={handleCreateReward}>
              Create Customized Reward
            </Button>
          </div>

          <ListingComponentWrapper
            customStyles={{ flex: 1 }}
            filterClasses="filter-styles"
            filterConfig={{
              withDebounce: {
                jsonSchema: customizeFilterDebounceJsonSchema,
                uiSchema: customizeFilterDebounceUiSchema,
              },
              withoutDebounce: {
                jsonSchema: customizedRewardsFilterJsonSchema,
                uiSchema: customizedRewardsFilterUiSchema,
              },
            }}
            toggleViewType={true}
            showAllFiltersOption={true}
            enableAllFiltersButton={false}
            offsetBasedPagination={true}
            viewOptions={["grid", "list"]}
          >
            {viewType === "list" ? (
              <CustomizedRewardsTable
                handleViewReward={handleViewReward}
                handleEditReward={handleEditReward}
                handleDeleteReward={handleDeleteReward}
                customizedRewardsData={processedRewardsData}
              />
            ) : (
              <CustomizedRewardsGrid
                handleViewReward={handleViewReward}
                handleEditReward={handleEditReward}
                handleDeleteReward={handleDeleteReward}
                customizedRewardsData={processedRewardsData}
              />
            )}
            {totalPages > 1 && (
              <div className="pagination-wrap mb-4">
                <Pagination
                  currentPage={currentPage}
                  onUpdate={handleChangePage}
                  pageCount={totalPages}
                  size="medium"
                  variant="number"
                />
              </div>
            )}
          </ListingComponentWrapper>
        </div>
      </div>
    </div>
  );
};

CustomizedRewardsListContent.propTypes = {
  rowsPerPage: PropTypes.number,
};

CustomizedRewardsListContent.defaultProps = {
  rowsPerPage: 10,
};

export default CustomizedRewardsListPage;
